import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { pharmacyAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Pill,
  Search,
  MapPin,
  Phone,
  Clock,
  Star,
  Truck,
  Upload,
  Camera,
  Loader2,
  ChevronRight,
  X,
  CheckCircle,
  AlertCircle,
  Package
} from 'lucide-react';
import { toast } from 'sonner';

// Status badges configuration
const STATUS_CONFIG = {
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  processing: { label: 'En traitement', color: 'bg-blue-100 text-blue-800', icon: Package },
  available: { label: 'Disponible', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  partial: { label: 'Partiellement disponible', color: 'bg-orange-100 text-orange-800', icon: AlertCircle },
  unavailable: { label: 'Non disponible', color: 'bg-red-100 text-red-800', icon: X },
  ready: { label: 'Prêt à récupérer', color: 'bg-purple-100 text-purple-800', icon: CheckCircle },
  completed: { label: 'Complété', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Annulé', color: 'bg-gray-100 text-gray-800', icon: X },
};

// Pharmacy Search Page
export const PharmacySearch = () => {
  const navigate = useNavigate();
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [deliveryOnly, setDeliveryOnly] = useState(false);

  useEffect(() => {
    loadPharmacies();
  }, [cityFilter, deliveryOnly]);

  const loadPharmacies = async () => {
    try {
      setLoading(true);
      const params = {};
      if (cityFilter) params.city = cityFilter;
      if (deliveryOnly) params.has_delivery = true;
      if (searchQuery) params.search = searchQuery;
      
      const response = await pharmacyAPI.search(params);
      setPharmacies(response.data.pharmacies || []);
    } catch (error) {
      console.error('Error loading pharmacies:', error);
      toast.error('Erreur de chargement des pharmacies');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadPharmacies();
  };

  const filteredPharmacies = pharmacies.filter(p => 
    !searchQuery || 
    p.pharmacy_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6 w-full max-w-full overflow-x-hidden" data-testid="pharmacy-search">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Pharmacies Partenaires
        </h1>
        <p className="text-[#64748B] mt-1">
          Envoyez votre ordonnance et récupérez vos médicaments
        </p>
      </div>

      {/* Search Form */}
      <Card className="mb-6 border-0 shadow-sm">
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher une pharmacie..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg text-sm"
            >
              <option value="">Toutes les villes</option>
              <option value="Yaoundé">Yaoundé</option>
              <option value="Douala">Douala</option>
            </select>
            <label className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={deliveryOnly}
                onChange={(e) => setDeliveryOnly(e.target.checked)}
                className="rounded"
              />
              <Truck className="w-4 h-4" />
              <span className="text-sm">Livraison</span>
            </label>
            <Button type="submit" className="bg-[#E53935] hover:bg-[#D32F2F]">
              Rechercher
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Pharmacies List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
        </div>
      ) : filteredPharmacies.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="text-center py-12">
            <Pill className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Aucune pharmacie trouvée</h3>
            <p className="text-slate-500 mt-2">
              Essayez avec d&apos;autres critères de recherche
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPharmacies.map((pharmacy) => (
            <Card 
              key={pharmacy.id}
              className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-all"
              onClick={() => navigate(`/pharmacies/${pharmacy.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Pill className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{pharmacy.pharmacy_name}</h3>
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <MapPin className="w-3 h-3" />
                        <span>{pharmacy.city}</span>
                      </div>
                    </div>
                  </div>
                  {pharmacy.has_delivery && (
                    <Badge className="bg-blue-100 text-blue-700">
                      <Truck className="w-3 h-3 mr-1" />
                      Livraison
                    </Badge>
                  )}
                </div>
                
                <p className="text-sm text-slate-600 mb-3 line-clamp-2">{pharmacy.address}</p>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-1 text-slate-500">
                    <Phone className="w-3 h-3" />
                    <span>{pharmacy.phone || 'Non renseigné'}</span>
                  </div>
                  {pharmacy.rating > 0 && (
                    <div className="flex items-center gap-1 text-amber-500">
                      <Star className="w-4 h-4 fill-current" />
                      <span className="font-medium">{pharmacy.rating}</span>
                      <span className="text-slate-400">({pharmacy.total_reviews})</span>
                    </div>
                  )}
                </div>
                
                {pharmacy.opening_hours && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-slate-400">
                    <Clock className="w-3 h-3" />
                    <span>{pharmacy.opening_hours}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Pharmacy Detail Page with Order Form
export const PharmacyDetail = () => {
  const { pharmacyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [pharmacy, setPharmacy] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadPharmacy();
  }, [pharmacyId]);

  const loadPharmacy = async () => {
    try {
      const response = await pharmacyAPI.getOne(pharmacyId);
      setPharmacy(response.data.pharmacy);
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error loading pharmacy:', error);
      toast.error('Pharmacie non trouvée');
      navigate('/pharmacies');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Format non supporté. Utilisez JPG, PNG, WebP ou PDF.');
        return;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Fichier trop volumineux (max 10 Mo)');
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } else {
        setPreviewUrl(null);
      }
    }
  };

  const handleSubmitOrder = async () => {
    if (!selectedFile) {
      toast.error('Veuillez ajouter une photo de votre ordonnance');
      return;
    }
    
    if (!user) {
      toast.error('Veuillez vous connecter pour envoyer une ordonnance');
      navigate('/login');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await pharmacyAPI.createOrder(pharmacyId, selectedFile, notes || null);
      toast.success('Ordonnance envoyée avec succès !');
      setShowOrderModal(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      setNotes('');
      
      // Navigate to orders
      navigate('/patient/prescriptions');
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
      </div>
    );
  }

  if (!pharmacy) {
    return null;
  }

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6" data-testid="pharmacy-detail">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/pharmacies')}
          className="mb-4"
        >
          ← Retour aux pharmacies
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <Pill className="w-8 h-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {pharmacy.pharmacy_name}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <MapPin className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">{pharmacy.address}, {pharmacy.city}</span>
              </div>
              {pharmacy.rating > 0 && (
                <div className="flex items-center gap-1 mt-2 text-amber-500">
                  <Star className="w-5 h-5 fill-current" />
                  <span className="font-semibold">{pharmacy.rating}</span>
                  <span className="text-slate-400">({pharmacy.total_reviews} avis)</span>
                </div>
              )}
            </div>
          </div>
          
          <Button
            onClick={() => setShowOrderModal(true)}
            className="bg-[#E53935] hover:bg-[#D32F2F] text-white"
            size="lg"
          >
            <Upload className="w-5 h-5 mr-2" />
            Envoyer mon ordonnance
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-[#E53935]" />
              <div>
                <p className="text-sm text-slate-500">Téléphone</p>
                <p className="font-semibold">{pharmacy.phone || 'Non renseigné'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-[#E53935]" />
              <div>
                <p className="text-sm text-slate-500">Horaires</p>
                <p className="font-semibold">{pharmacy.opening_hours || 'Non renseigné'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Truck className="w-5 h-5 text-[#E53935]" />
              <div>
                <p className="text-sm text-slate-500">Livraison</p>
                <p className="font-semibold">{pharmacy.has_delivery ? 'Disponible' : 'Non disponible'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* How it works */}
      <Card className="border-0 shadow-sm mb-8">
        <CardHeader>
          <CardTitle className="text-lg">Comment ça marche ?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[
              { step: 1, title: 'Envoyez', desc: 'Prenez une photo de votre ordonnance' },
              { step: 2, title: 'Attendez', desc: 'La pharmacie vérifie la disponibilité' },
              { step: 3, title: 'Confirmation', desc: 'Vous recevez le prix et la disponibilité' },
              { step: 4, title: 'Récupérez', desc: 'Payez sur place et récupérez vos médicaments' },
            ].map((item, idx) => (
              <div key={item.step} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#E53935] text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900">{item.title}</h4>
                  <p className="text-sm text-slate-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Reviews */}
      {reviews.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg">Avis clients ({reviews.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="border-b pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium">{review.patient_name}</span>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${i < review.rating ? 'text-amber-400 fill-current' : 'text-slate-200'}`}
                        />
                      ))}
                    </div>
                  </div>
                  {review.comment && (
                    <p className="text-sm text-slate-600">{review.comment}</p>
                  )}
                  <p className="text-xs text-slate-400 mt-1">
                    {new Date(review.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Order Modal */}
      <Dialog open={showOrderModal} onOpenChange={setShowOrderModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Envoyer mon ordonnance</DialogTitle>
            <DialogDescription>
              Prenez une photo claire de votre ordonnance. La pharmacie vous contactera pour confirmer la disponibilité.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* File Upload */}
            <div 
              className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-[#E53935] transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              {previewUrl ? (
                <div className="relative">
                  <img src={previewUrl} alt="Preview" className="max-h-48 mx-auto rounded" />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setPreviewUrl(null);
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : selectedFile ? (
                <div className="flex items-center justify-center gap-2">
                  <Package className="w-8 h-8 text-slate-400" />
                  <span className="text-sm text-slate-600">{selectedFile.name}</span>
                </div>
              ) : (
                <>
                  <Camera className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-600">Cliquez pour ajouter une photo</p>
                  <p className="text-xs text-slate-400">JPG, PNG, WebP ou PDF (max 10 Mo)</p>
                </>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
            
            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-slate-700">Notes (optionnel)</label>
              <Textarea
                placeholder="Ex: Je suis allergique à la pénicilline..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOrderModal(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleSubmitOrder}
              disabled={!selectedFile || submitting}
              className="bg-[#E53935] hover:bg-[#D32F2F]"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Envoi...
                </>
              ) : (
                'Envoyer'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Patient Prescriptions List
export const PatientPrescriptions = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await pharmacyAPI.getPatientOrders();
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    if (filter === 'active') return !['completed', 'cancelled'].includes(order.status);
    if (filter === 'completed') return order.status === 'completed';
    return true;
  });

  const handleCancel = async (orderId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette commande ?')) return;
    
    try {
      await pharmacyAPI.cancelOrder(orderId);
      toast.success('Commande annulée');
      loadOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6" data-testid="patient-prescriptions">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Mes Ordonnances
          </h1>
          <p className="text-[#64748B] mt-1">
            Suivez vos commandes en pharmacie
          </p>
        </div>
        <Button
          onClick={() => navigate('/pharmacies')}
          className="bg-[#E53935] hover:bg-[#D32F2F]"
        >
          <Pill className="w-4 h-4 mr-2" />
          Nouvelle ordonnance
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {['all', 'active', 'completed'].map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? 'bg-[#E53935]' : ''}
          >
            {f === 'all' ? 'Toutes' : f === 'active' ? 'En cours' : 'Terminées'}
          </Button>
        ))}
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="text-center py-12">
            <Package className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700">Aucune ordonnance</h3>
            <p className="text-slate-500 mt-2">
              Envoyez votre première ordonnance à une pharmacie partenaire
            </p>
            <Button
              onClick={() => navigate('/pharmacies')}
              className="mt-4 bg-[#E53935] hover:bg-[#D32F2F]"
            >
              Trouver une pharmacie
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map((order) => {
            const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card key={order.id} className="border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                        {order.prescription_image_url && (
                          <img 
                            src={order.prescription_image_url} 
                            alt="Ordonnance"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{order.pharmacy_name}</h3>
                        <p className="text-sm text-slate-500">{order.pharmacy_address}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                          {order.total_price && (
                            <span className="font-semibold text-[#E53935]">
                              {order.total_price.toLocaleString()} FCFA
                            </span>
                          )}
                        </div>
                        {order.pharmacy_response && (
                          <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded">
                            <strong>Réponse:</strong> {order.pharmacy_response}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className="text-xs text-slate-400">
                        {new Date(order.created_at).toLocaleDateString('fr-FR')}
                      </span>
                      {!['completed', 'cancelled'].includes(order.status) && (
                        <div className="flex gap-2">
                          {order.pharmacy_phone && (
                            <a href={`tel:${order.pharmacy_phone}`}>
                              <Button variant="outline" size="sm">
                                <Phone className="w-4 h-4" />
                              </Button>
                            </a>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleCancel(order.id)}
                          >
                            Annuler
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PharmacySearch;
